import { useState } from 'react'
import type { ClientAction, PlayerView } from '@sh/shared'
import { usePendingCard } from '../../components/Nudge.js'
import { Veil } from '../../components/Veil.js'
import { WaitingOn } from '../../components/Waiting.js'
import { useAsset } from '../../skin/SkinContext.js'

/**
 * Roles are shown once, privately, behind a press-and-hold, and are hidden
 * again a moment after the finger lifts. Nothing is ever left on screen.
 *
 * This is also the only screen that ever names the other fascists. The server
 * stops sending them the moment this phase ends, so the warning below is not a
 * convention the client is politely observing - after this, the information is
 * gone from the wire entirely.
 */
export function RevealPhase({
  view,
  act,
}: {
  view: PlayerView
  act: (a: ClientAction) => void
}) {
  const you = view.you
  const card = useAsset(`role.${you?.role ?? 'liberal'}`)
  const name = (id: string) => view.players.find((p) => p.id === id)?.name ?? '?'
  const acked = you ? view.revealAck.includes(you.id) : false
  // Saying you have seen a role you never looked at is the one way to start
  // the game without knowing your side, so the button waits for a hold.
  const [seen, setSeen] = useState(false)
  // Nine people staring at a lobby that will not deal because one phone is
  // face-down on the table is the longest dead stop the game has.
  const pending = usePendingCard(!acked)
  const ready = view.revealAck.length
  const total = view.players.length

  /**
   * Everyone on your side that this table lets you know about, as rows rather
   * than as a sentence. Hitler is one of them: he is not in `knownFascists`,
   * which is the fascists proper, and reading "Hitler is Ana" off the end of a
   * comma-separated list was the single most-missed line in the game - people
   * held the card, read their own role, let go, and started the round with no
   * idea who they were playing with.
   */
  const team = you?.knownFascists
    ? [
        ...you.knownFascists.map((id) => ({ id, hitler: false })),
        ...(you.knownHitler ? [{ id: you.knownHitler, hitler: true }] : []),
      ].filter((row) => row.id !== you.id)
    : []

  return (
    <div className="stack">
      {/*
        Carrying the same gold frame as every other card the table is waiting on
        you for. This one is asking for something - read this, then say you have
        - and on a wide screen, where it sits in a column beside a board that
        fills the rest of the window, an unframed card was not obviously the
        half of the screen with the work in it.
      */}
      <section ref={pending.ref} className={`card card--reveal ${pending.className}`}>
        <h2 className="card__title">Your secret role</h2>
        <p className="card__hint">
          Hold to read it. It disappears a moment after you let go, or at once if you switch away.
        </p>
        {you?.knownFascists && (
          <p className="card__warn" data-testid="team-warning">
            <strong>Read this carefully now.</strong> This is the only time you will be shown who
            your fascists are. From here your phone knows nothing but your own role — the table is
            yours to remember, exactly as it would be with the cards face down.
          </p>
        )}

        <Veil
          label="Hold to reveal your role"
          tone={you?.party === 'liberal' ? 'liberal' : 'fascist'}
          frame="card"
          art={Boolean(card)}
          team={team.length > 0}
          onReveal={() => setSeen(true)}
          testId="role-reveal"
        >
          {card && <img className="reveal__card" src={card} alt="" aria-hidden="true" draggable={false} />}
          <span className="reveal__role">
            {you?.role === 'hitler' ? 'Hitler' : you?.role === 'fascist' ? 'Fascist' : 'Liberal'}
          </span>
          <span className="reveal__party">
            {you?.party === 'liberal' ? 'Liberal party' : 'Fascist party'}
          </span>

          {team.length > 0 ? (
            <span className="team">
              <span className="team__heading">Your team</span>
              <span className="team__list">
                {team.map((row) => (
                  <span key={row.id} className="team__row">
                    <span className="team__name">{name(row.id)}</span>
                    <span
                      className={`badge badge--role badge--${row.hitler ? 'hitler' : 'fascist'}`}
                    >
                      {row.hitler ? 'Hitler' : 'Fascist'}
                    </span>
                  </span>
                ))}
              </span>
            </span>
          ) : you?.role === 'hitler' ? (
            <span className="reveal__team">
              At this table size you do not learn who the fascists are. They know you.
            </span>
          ) : (
            <span className="reveal__team">You know nothing about anyone else. Good luck.</span>
          )}
        </Veil>

        <button
          type="button"
          className="btn btn--primary btn--block"
          disabled={acked || !seen}
          onClick={() => act({ type: 'ack_reveal' })}
          data-testid="ack-reveal"
        >
          {acked ? 'Waiting for the others…' : 'I have seen my role'}
        </button>

        {acked ? (
          <WaitingOn testId="reveal-progress">
            {ready} of {total} ready — waiting for the rest of the table
          </WaitingOn>
        ) : (
          <p className="waiting" data-testid="reveal-progress">
            {ready} of {total} ready
          </p>
        )}

        {/*
          The last chance, and it says so. The card is still holdable until the
          final person is ready; the moment the phase ends the server stops
          sending the names and there is nothing left here to re-open.
        */}
        {you?.knownFascists && (
          <p className="card__warn card__warn--quiet">
            Hold the card again as many times as you need. When the last player is ready, your team
            is gone from this phone for good.
          </p>
        )}
      </section>
    </div>
  )
}
